import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useOrganization, useUser } from "@clerk/clerk-react";
import { useApiFetch } from "src/lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { CalendarIcon, Video } from "lucide-react";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
// [tutorial disabled] import { driver } from "driver.js";
// [tutorial disabled] import "driver.js/dist/driver.css";
import LoadingIndicator from "../components/common/LoadingIndicator";
import "../styles/Detection.css";

/*
 * Detection video. Nothing heavy is downloaded until it is needed:
 *   - with a thumbnail: only the (lazy) image; the video is created when the user presses play
 *   - without a thumbnail: the video metadata (first frame) loads only once the tile is on screen
 */
const VideoPlayer = ({ streamUrl, miniature }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [inView, setInView] = useState(false);
  // True while the visible media (thumbnail, first frame or playback) has not loaded yet
  const [mediaLoading, setMediaLoading] = useState(true);
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const hasThumbnail = Boolean(miniature);

  useEffect(() => {
    if (hasThumbnail || inView) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start a little before it scrolls into view
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasThumbnail, inView]);

  const handlePlayClick = () => {
    setIsPlaying(true);
    setMediaLoading(true);
    requestAnimationFrame(() => {
      videoRef.current?.play().catch((err) => {
        console.error("Autoplay failed:", err);
        setMediaLoading(false); // The controls are shown, the user can start it manually
      });
    });
  };

  const showVideo = isPlaying || (!hasThumbnail && inView);

  return (
    <div className="detections-media" ref={containerRef}>
      {showVideo ? (
        <video
          ref={videoRef}
          src={streamUrl}
          controls={isPlaying}
          muted
          playsInline
          preload={isPlaying ? "auto" : "metadata"}
          onLoadedMetadata={() => {
            // Show the first frame instead of a black tile
            if (videoRef.current && !isPlaying) videoRef.current.currentTime = 0.1;
          }}
          onLoadedData={() => !isPlaying && setMediaLoading(false)}
          onWaiting={() => setMediaLoading(true)}
          onPlaying={() => setMediaLoading(false)}
          onError={() => setMediaLoading(false)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        >
          Your browser does not support the video tag.
        </video>
      ) : (
        !hasThumbnail && <div className="skeleton-block absolute inset-0 rounded-none" />
      )}

      {mediaLoading && (showVideo || hasThumbnail) && (
        <LoadingIndicator variant="tile" label={isPlaying ? "Loading video…" : null} />
      )}

      {!isPlaying && (
        <button
          type="button"
          onClick={handlePlayClick}
          aria-label="Play video"
          style={{
            border: "none",
            padding: 0,
            margin: 0,
            background: "none",
            cursor: "pointer",
            width: "100%",
            height: "100%",
            position: "absolute",
            inset: 0,
          }}
        >
          {hasThumbnail && (
            <img
              src={miniature}
              alt="Video thumbnail"
              loading="lazy"
              decoding="async"
              onLoad={() => setMediaLoading(false)}
              onError={() => setMediaLoading(false)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}

          {/* Play icon overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(0deg, rgba(0,0,0,0.4), transparent)",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  marginLeft: 3,
                  width: 0,
                  height: 0,
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                  borderLeft: "12px solid white",
                }}
              />
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

/* Placeholder cards with the page structure, shown while the detections load */
const DetectionsSkeleton = () => (
  <div className="detections-list" aria-busy="true" aria-label="Loading bird detections">
    {[0, 1].map((card) => (
      <Card key={card} className="detections-card overflow-hidden">
        <CardHeader className="p-4 sm:p-6">
          <div className="skeleton-block h-6 w-48 max-w-full" />
        </CardHeader>
        <CardContent className="detections-grid-wrap p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="detections-grid">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="detections-item p-2">
                <div className="skeleton-block w-full" style={{ aspectRatio: "4 / 3" }} />
                <div className="skeleton-block mt-2 h-4 w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

const INITIAL_ROWS = 2; // Rows of videos shown per camera before "Load more"
const ROWS_PER_LOAD = 4; // Rows added by each "Load more"

/* Number of columns the CSS grid currently renders (it changes with the card width) */
const useGridColumns = (gridRef) => {
  const [columns, setColumns] = useState(4);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const measure = () => {
      const count = getComputedStyle(el).gridTemplateColumns.split(" ").filter(Boolean).length;
      setColumns(Math.max(1, count));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [gridRef]);

  return columns;
};

/* Videos of one camera: shows INITIAL_ROWS rows, each "Load more" adds ROWS_PER_LOAD rows */
const DetectionVideos = ({ videos, monitorId, onDelete }) => {
  const gridRef = useRef(null);
  const columns = useGridColumns(gridRef);
  const [rows, setRows] = useState(INITIAL_ROWS);

  const visibleCount = rows * columns;
  const hasMore = visibleCount < videos.length;
  const canShowLess = rows > INITIAL_ROWS && videos.length > INITIAL_ROWS * columns;

  return (
    <>
      <div className="detections-grid" ref={gridRef}>
        {videos.length === 0 ? (
          <p className="col-span-full">No videos available for this camera.</p>
        ) : (
          videos.slice(0, visibleCount).map((video, index) => {
            const dateLabel = `Date: ${video?.time ? format(new Date(video.time), "PPP") : "-"}`;
            return (
              <div key={video?.filename ?? index} className="detections-item p-2">
                <VideoPlayer
                  streamUrl={video?.actionUrl}
                  miniature={video?.miniature}
                />
                <div className="detections-item-meta">
                  <h3 className="detections-item-date text-sm p-1" title={dateLabel}>
                    {dateLabel}
                  </h3>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-10 w-10 shrink-0 p-0"
                        aria-label="Video actions"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end"  className="z-[10000]">
                      <DropdownMenuItem
                        onClick={() => onDelete(video?.filename, monitorId)}
                        className="min-h-10"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })
        )}
      </div>

      {(hasMore || canShowLess) && (
        <div className="detections-load-actions">
          {hasMore && (
            <Button variant="outline" className="h-10" onClick={() => setRows((prev) => prev + ROWS_PER_LOAD)}>
              Load more ({videos.length - visibleCount})
            </Button>
          )}
          {canShowLess && (
            <Button variant="ghost" className="h-10" onClick={() => setRows(INITIAL_ROWS)}>
              Show less
            </Button>
          )}
        </div>
      )}
    </>
  );
};

const Detections = () => {
  const { organization } = useOrganization();
  const [detections, setLivestreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cameraFilter, setCameraFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const orgId = organization?.id;
  const { user } = useUser();
  const [role, setRole] = useState("member");

  // const role = user.organizationMemberships[0].role;

  const navigate = useNavigate(); // Hook para redirección
  const apiFetch = useApiFetch();

  // [tutorial disabled] const disableTutorial = async () => {
    // [tutorial disabled] try {
      // [tutorial disabled] const data = await apiFetch("/auth/disable-tutorial", { method: "POST" });
      // [tutorial disabled] if (!data) return;

      // [tutorial disabled] setRole(data?.role || "member");
      
    // [tutorial disabled] } catch (error) {
      // [tutorial disabled] console.error("Error disabling tutorial:", error);
    // [tutorial disabled] }
  // [tutorial disabled] };

  // [tutorial disabled] /* Display tour tutorial */
  // [tutorial disabled] useEffect(() => {
    // [tutorial disabled] let intervalId = null;

    // [tutorial disabled] const checkForTutorialReady = () => {
      // [tutorial disabled] let step = localStorage.getItem("driver-tutorial-step");
      // [tutorial disabled] const el = document.querySelector("#detections");

      // [tutorial disabled] if (step === "detections" && el) {
        // [tutorial disabled] clearInterval(intervalId); // limpiar el polling
        // [tutorial disabled] startDriver(); // lanzar el tour

        // [tutorial disabled] setTimeout(() => {
          // [tutorial disabled] intervalId = setInterval(checkForTutorialReady, 300);
        // [tutorial disabled] }, 1000);
      // [tutorial disabled] }
    // [tutorial disabled] };

    // [tutorial disabled] const startDriver = () => {
      // [tutorial disabled] const driverObj = driver({
        // [tutorial disabled] steps: [
          // [tutorial disabled] {
            // [tutorial disabled] element: "#detections",
            // [tutorial disabled] popover: {
              // [tutorial disabled] title: "Detections",
              // [tutorial disabled] description: "This section retrieves video detections from the devices",
                // [tutorial disabled] prevBtnText:  `
                  // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    // [tutorial disabled] <path d="M15 18l-6-6 6-6" />
                  // [tutorial disabled] </svg>
                // [tutorial disabled] `,
                // [tutorial disabled] doneBtnText:  `
                    // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                    // [tutorial disabled] </svg>
                  // [tutorial disabled] `,
                  // [tutorial disabled] nextBtnText:  `
                    // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                    // [tutorial disabled] </svg>
                  // [tutorial disabled] `,
              // [tutorial disabled] onCloseClick: () => {
                // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
                // [tutorial disabled] driverObj.destroy();
                // [tutorial disabled] disableTutorial();
              // [tutorial disabled] },
              // [tutorial disabled] onPrevClick: () => {
                // [tutorial disabled] navigate("/livestreaming");
                // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "livestreaming");
              // [tutorial disabled] },   
            // [tutorial disabled] },
            // [tutorial disabled] onDeselected: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
              // [tutorial disabled] disableTutorial();
            // [tutorial disabled] },
            // [tutorial disabled] onDestroyed: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
              // [tutorial disabled] disableTutorial();
            // [tutorial disabled] },
            // [tutorial disabled] onHighlighted: () => {
              // [tutorial disabled] const enablePrev = () => {
                // [tutorial disabled] const prevBtn = document.querySelector('.driver-popover-prev-btn');
                // [tutorial disabled] if (prevBtn) {
                  // [tutorial disabled] prevBtn.removeAttribute("disabled");
                  // [tutorial disabled] prevBtn.classList.remove('driver-popover-btn-disabled');
                  // [tutorial disabled] clearInterval(retry); // Detener intentos
                // [tutorial disabled] }
              // [tutorial disabled] };

              // [tutorial disabled] const retry = setInterval(enablePrev, 100);
              // [tutorial disabled] // opcionalmente corta después de 1s si no se logra
              // [tutorial disabled] setTimeout(() => clearInterval(retry), 1000);
            // [tutorial disabled] },
          // [tutorial disabled] },
          // [tutorial disabled] {
            // [tutorial disabled] element: "#filter-bar",
            // [tutorial disabled] popover: {
              // [tutorial disabled] title: "Bar to filter detections by name or date",
              // [tutorial disabled] prevBtnText:  `
                  // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    // [tutorial disabled] <path d="M15 18l-6-6 6-6" />
                  // [tutorial disabled] </svg>
                // [tutorial disabled] `,
                // [tutorial disabled] doneBtnText:  `
                    // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                    // [tutorial disabled] </svg>
                  // [tutorial disabled] `,
                  // [tutorial disabled] nextBtnText:  `
                    // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                    // [tutorial disabled] </svg>
                  // [tutorial disabled] `,
              // [tutorial disabled] onNextClick: () => {
                // [tutorial disabled] /* localStorage.setItem("driver-tutorial-step", "device-manager");
                // [tutorial disabled] navigate("/devicemanager"); */
                // [tutorial disabled] if (role === "admin") {
                  // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "device-manager");
                  // [tutorial disabled] navigate("/devicemanager");
                // [tutorial disabled] } else {
                  // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "dashboard-v1");
                  // [tutorial disabled] navigate("/dashboard");
                // [tutorial disabled] }
              // [tutorial disabled] },
            // [tutorial disabled] },
            // [tutorial disabled] onDeselected: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
              // [tutorial disabled] disableTutorial();
            // [tutorial disabled] },
            // [tutorial disabled] onDestroyed: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
              // [tutorial disabled] disableTutorial();
            // [tutorial disabled] },
          // [tutorial disabled] },
          
        // [tutorial disabled] ],
      // [tutorial disabled] });

      // [tutorial disabled] driverObj.drive();
    // [tutorial disabled] };

    // [tutorial disabled] // Poll cada 300ms hasta que estén el paso y el elemento
    // [tutorial disabled] intervalId = setInterval(checkForTutorialReady, 300);

    // [tutorial disabled] // Limpieza del intervalo
    // [tutorial disabled] return () => {
      // [tutorial disabled] if (intervalId) clearInterval(intervalId);
    // [tutorial disabled] };
  // [tutorial disabled] }, []);

  const fetchDetections = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/api/detections");
      if (!data) return;
      setLivestreams(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching livestreams:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVideo = async (filename, monitorId) => {
    // 1. Confirmación (dialog) antes de eliminar
    const confirmed = window.confirm(
      `Are you sure you want to delete the video: ${filename}?`
    );
    if (!confirmed) return;

    // 2. Llamada a la API (método POST)
    try {
      const data = await apiFetch("/api/detections-delete", {
        method: "POST",
        body: {
          monitorId: monitorId,
          filename: filename,
        },
      });
      if (!data) return;
      console.log(
        `[INFO] Video ${filename} (monitor: ${monitorId}) deleted successfully`
      );
      // 3. Actualizar el estado local para que desaparezca el video
      setLivestreams((prevDetections) =>
        (prevDetections ?? [])
          .filter((stream) => stream) // Elimina objetos nulos
          .map((stream) => {
            if (stream.monitorId === monitorId) {
              const newStreamUrl = (stream?.streamUrl ?? []).filter(
                (video) => video?.filename !== filename
              );
              return { ...stream, streamUrl: newStreamUrl };
            }
            return stream;
          })
      );
    } catch (error) {
      console.error("Error deleting video:", error);
      alert("Failed to delete the video. Please try again.");
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await fetchDetections(); // Only fetch detections if orgId is available
    };
    fetchData();
  }, [orgId]);

  const filteredDetections = (detections ?? [])
    .filter((stream) => stream && stream.name)
    .filter((stream) =>
      stream.name.toLowerCase().includes(cameraFilter.toLowerCase())
    )
    .filter((stream) => {
      if (!dateRange.from && !dateRange.to) return true;
      
      return (stream?.streamUrl ?? []).some((video) => {
        const videoDate = new Date(video?.time);

        if (dateRange.from && dateRange.to) {
          return videoDate >= dateRange.from && videoDate <= dateRange.to;
          
        } else if (dateRange.from) {
          return videoDate >= dateRange.from;

        } else if (dateRange.to) {
          return videoDate <= dateRange.to;
        }

        return true;
      });
    });

  const clearFilters = () => {
    setCameraFilter("");
    setDateRange({ from: null, to: null });
  };

  return (
    <div className="detections-page container mx-auto px-2 py-8" >
      <div className="detections-filter-bar mb-6" id="filter-bar">
        <Input
          type="text"
          placeholder="Filter by camera name"
          value={cameraFilter}
          onChange={(e) => setCameraFilter(e.target.value)}
          className="detections-filter-input h-10"
        />
        <Popover className="absolute z-1000">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="detections-date-trigger h-10 justify-start text-left font-normal"
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              {dateRange?.from ? (
                dateRange?.to ? (
                  <span>
                    {format(dateRange.from, "LLL dd, y")} -{" "}
                    {format(dateRange.to, "LLL dd, y")}
                  </span>
                ) : (
                  <span>{format(dateRange.from, "LLL dd, y")}</span>
                )
              ) : (
                <span>Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" sideOffset={4} collisionPadding={8} className="detections-calendar-popover z-[9999] w-auto" asChild>
            <div className="relative">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from || new Date()}
                selected={{
                  from: dateRange?.from || undefined,
                  to: dateRange?.to || undefined,
                }}
                onSelect={(range) => {
                  setDateRange({
                    from: range?.from || null,
                    to: range?.to || null,
                  });
                }}
                numberOfMonths={2}
              />
            </div>
          </PopoverContent>

        </Popover>
        <Button onClick={clearFilters} variant="outline" className="h-10" >
          Clear Filters
        </Button>
      </div>

      {loading ? (
        <>
          <LoadingIndicator
            label="Loading bird detections…"
            hint="Videos are loaded progressively as you scroll."
          />
          <DetectionsSkeleton />
        </>
      ) : (
        <div className={`${filteredDetections.length === 0 ? "grid w-full gap-6 md:grid-cols-2 lg:grid-cols-3" : "detections-list"}`}  id="detections">
          {filteredDetections.length === 0 ? (
            <Card className="">
              <CardContent className="flex h-40 items-center justify-center">
                <p className="text-center text-muted-foreground">
                  No bird detections found for the selected filters.
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredDetections.map((stream) => {
              // Filter and limit videos for each stream
              const filteredVideos = (stream.streamUrl || [])
                .filter((video) => {
                  const videoDate = new Date(video?.time);
                  if (dateRange.from && dateRange.to) {
                    return (
                      videoDate >= dateRange.from && videoDate <= dateRange.to
                    );
                  } else if (dateRange.from) {
                    return videoDate >= dateRange.from;
                  } else if (dateRange.to) {
                    return videoDate <= dateRange.to;
                  }
                  return true;
                })
                .slice(0);

              return (
                <Card key={stream.monitorId} className="detections-card overflow-hidden z-0">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="detections-card-title flex items-center space-x-2">
                      <Video className="h-5 w-5 shrink-0" />
                      <span title={stream.name}>{stream.name}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="detections-grid-wrap p-4 pt-0 sm:p-6 sm:pt-0">
                    <DetectionVideos
                      videos={filteredVideos}
                      monitorId={stream.monitorId}
                      onDelete={handleDeleteVideo}
                    />
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default Detections;